import { AuthenticationService } from './../../../../core/services/authentication.service';
import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray, ValidationErrors, AbstractControl, ValidatorFn } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpClient } from '@angular/common/http';
import { NgxSpinnerService } from 'ngx-spinner';
import { EnvironmentService } from 'src/app/core/services/environment.service';
import { NodeService } from 'src/app/core/services/node.service';
import { Node } from "src/app/core/models/node";
import { Deployment } from 'src/app/core/models/deployment';
import { DeploymentService } from 'src/app/core/services/deployment.service';

interface PodConfig {
  namespace: string;
  name?: string;
  image: string;
  resources: {
    requests: { memory: string; cpu: string; 'nvidia.com/gpu': string };
    limits: { memory: string; cpu: string; 'nvidia.com/gpu': string };
  };
  env: Array<{ name: string; value: string }>;
  userName: string;
  args: string[];
  nodeHostnames?: string[];
  packages?: string[];
}

@Component({
  selector: "app-create-pod-dialog",
  templateUrl: "./create-pod-dialog.component.html",
  styleUrls: ["./create-pod-dialog.component.scss"],
})
export class CreatePodDialogComponent implements OnInit {
  podForm: FormGroup;
  isCreating = false;
  predefinedImages = [
    { value: 'nvidia/cuda:11.0.3-base-ubuntu20.04', label: 'NVIDIA CUDA 11.0.3' },
    { value: 'ubuntu:latest', label: 'Ubuntu Latest' },
    { value: 'alpine:latest', label: 'Alpine Latest' },
    { value: 'busybox:latest', label: 'Busybox Latest' },
    { value: 'node:18', label: 'Node.js 18' },
    { value: 'python:3.10', label: 'Python 3.10' },
    { value: 'startx/alma:latest', label: 'Alma Linux' },
    { value: 'custom', label: 'Custom...' }
  ];
  cpuOptions = [
    { value: "500m/1000m", label: "500m/1000m" },
    { value: "1000m/2000m", label: "1000m/2000m" },
  ];
  gpuOptions = [
    { value: '1/1', label: '1/1' }
  ];
  memoryOptions = [
    { value: '512Mi/1000Mi', label: '512Mi/1000Mi' },
    { value: '1Gi/2Gi', label: '1Gi/2Gi' },
    { value: '2Gi/4Gi', label: '2Gi/4Gi' },
    { value: '4Gi/8Gi', label: '4Gi/8Gi' },
  ];
  namespaceOptions = ["default"];
  depNodes = [];
  deployment: Deployment;

  constructor(
    public dialogRef: MatDialogRef<CreatePodDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      headNodeIp: string,
      depName: string
    },
    private fb: FormBuilder,
    private http: HttpClient,
    private snackBar: MatSnackBar,
    private spinner: NgxSpinnerService,
    private authenticationService: AuthenticationService,
    private _env: EnvironmentService,
    private nodeService: NodeService,
    private depService: DeploymentService
  ) {
    this.podForm = this.fb.group({
      namespace: ['default', Validators.required],
      name: ['', [Validators.required, Validators.pattern('^[a-z0-9]([-a-z0-9]*[a-z0-9])?$')]],
      image: ['nvidia/cuda:11.0.3-base-ubuntu20.04', Validators.required],
      customImage: [''],
      gpu: ['1/1', this.gpuValidator()],
      cpu: ['500m/1000m',[Validators.required, this.cpuValidator()]],
      memory: ['512Mi/1000Mi', [Validators.required, this.memoryValidator()]],
      commandSequence: [''],
      nodeNames: [],
      env: this.fb.array([]),
    });
  }

  ngOnInit(): void {
    let urlStatus: boolean = this.checkUrl();
    if (urlStatus) {
      // Watch for image changes to show/hide custom image input
      this.podForm.get("image")?.valueChanges.subscribe((value) => {
        if (value === "custom") {
          this.podForm.get("customImage")?.setValidators([Validators.required]);
        } else {
          this.podForm.get("customImage")?.clearValidators();
        }
        this.podForm.get("customImage")?.updateValueAndValidity();
      });

      this.podForm.controls['namespace'].markAsUntouched();

      try {
        this.spinner.show('please-wait');
        if (this.data.depName) {
          this.depService.getDeployment(this.data.depName).subscribe({
            next: (dep) => {
              if (dep) {
                this.deployment = <Deployment>JSON.parse(JSON.stringify(dep));
                if (this.deployment) {
                  if (this.deployment.computeNodeNames) {
                    this.depNodes = this.deployment.computeNodeNames.split(',').map(name => name.trim());
                    this.depNodes.unshift("any");
                    this.podForm.controls['nodeNames'].setValue([this.depNodes[0]]);
                  }

                  if (this.deployment.nodeGroup) {
                    this.namespaceOptions = this.deployment.nodeGroup.split(',').map(ng => ng.trim());
                  }
                  this.spinner.hide('please-wait');
                }
              }
            },
            error: (error) => {
              this.spinner.hide('please-wait');
              console.error('Error fetching deployment:', error);
            }
          });
        }
        else {
          this.spinner.hide('please-wait');
        }
      }
      catch (error) {
        this.spinner.hide('please-wait');
      }
    }
  }

  get envArray(): FormArray {
    return this.podForm.get("env") as FormArray;
  }

  addEnvVar(): void {
    this.envArray.push(
      this.fb.group({
        name: ["", Validators.required],
        value: [""],
      })
    );
  }

  removeEnvVar(index: number): void {
    this.envArray.removeAt(index);
  }

  async createPod(): Promise<void> {
    if (this.podForm.invalid) {
      this.snackBar.open("Please fill in all required fields", "OK", {
        duration: 3000,
      });
      return;
    }

    this.isCreating = true;
    this.spinner.show('create-pod');

    const cpuValues = this.podForm.value.cpu.split('/');
    const memoryValues = this.podForm.value.memory.split('/');

    const image = this.podForm.value.image === 'custom'
      ? this.podForm.value.customImage
      : this.podForm.value.image;

    let args = ['-c', 'tail -f /dev/null'];
    let packages = [];

    if (this.podForm.value.commandSequence) {
      const commandSequence = this.podForm.value.commandSequence.split(' ');
      if (commandSequence != null && commandSequence.length > 0) {
        packages = commandSequence;
      }
    }

    const config: PodConfig = {
      namespace: this.podForm.value.namespace,
      name: this.podForm.value.name || undefined,
      image: image,
      resources: {
        requests: {
          cpu: cpuValues[0],
          memory: memoryValues[0],
          'nvidia.com/gpu': this.podForm.value.gpu.split('/')[0] ? this.podForm.value.gpu.split('/')[0] : undefined
        },
        limits: {
          cpu: cpuValues[1],
          memory: memoryValues[1],
          'nvidia.com/gpu': this.podForm.value.gpu.split('/')[1] ? this.podForm.value.gpu.split('/')[1] : undefined
        }
      },
      env: this.podForm.value.env.filter((env: any) => env.name),
      userName: this.authenticationService.loggedInShortUserName,
      args: args,
      nodeHostnames: this.podForm.value.nodeNames && this.podForm.value.nodeNames.length == 1 && this.podForm.value.nodeNames[0] === "any" ? 
        this.depNodes.filter(node => node !== "any") : this.podForm.value.nodeNames || this.depNodes.filter(node => node !== "any"),
      packages: packages,
    };

    try {
      const serverUrl = 'https://' + this.data.headNodeIp + ':' + this._env.envVariables.UtilityAccessPort;
      const response = await this.http.post<any>(
        `${serverUrl}/api/pods`,
        config
      ).toPromise();

      if (response?.success) {
        this.snackBar.open(`Pod created successfully: ${response.namespace}/${response.podName}`, 'OK', {
          duration: 5000
        });

        this.dialogRef.close({
          success: true,
          namespace: response.namespace,
          podName: response.podName,
        });
      }
      else {
        this.snackBar.open(`${response?.message || 'Unknown error'}`, 'OK', {
          duration: 5000
        });
      }
    } catch (error: any) {
      this.snackBar.open(`Error: ${error.error?.message || error.message}`, 'OK', {
        duration: 5000
      });
    } finally {
      this.isCreating = false;
      this.spinner.hide("create-pod");
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }

  checkUrl() {
    fetch('https://' + this.data.headNodeIp + ':' + this._env.envVariables.UtilityAccessPort + "/health", { mode: 'no-cors' })
      .then(() => {
        return true;
      })
      .catch((err) => {
        this.snackBar
          .open(
            "Please check pod manager status first before requesting pod",
            "OK",
            {}
          )
          .afterDismissed()
          .subscribe(() => {
            window.open(
              "https://" +
              this.data.headNodeIp +
              ":" +
              this._env.envVariables.UtilityAccessPort +
              "/health",
              "_blank"
            );
          });
        return false;
      });
    return true;
  }

  cpuValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) return null;

      const regex = /^(\d+)m\/(\d+)m$/;   // only integers + 'm'
      const match = regex.exec(value);
      if (!match) return { invalidCpu: true };

      const left = parseInt(match[1], 10);
      const right = parseInt(match[2], 10);
      return left < right || left == right ? null : { invalidCpu: true };
    };
  }

  gpuValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) return null;

      const regex = /^(\d+)\/(\d+)$/;   // only integers
      const match = regex.exec(value);
      if (!match) return { invalidGpu: true };

      const left = parseInt(match[1], 10);
      const right = parseInt(match[2], 10);
      return left < right || left == right ? null : { invalidGpu: true };
    };
  }

  // Memory Validator: e.g. 512Mi/1Gi
  memoryValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) return null;

      // Require same unit on both sides (either Mi/Mi or Gi/Gi)
      const regex = /^(\d+)(Mi|Gi)\/(\d+)(Mi|Gi)$/;
      const match = regex.exec(value);
      if (!match) return { invalidMemory: true };

      const leftVal = parseInt(match[1], 10);
      const leftUnit = match[2];
      const rightVal = parseInt(match[3], 10);
      const rightUnit = match[4];

      // units must match (Mi with Mi or Gi with Gi)
      if (leftUnit !== rightUnit) return { invalidMemory: true };

      // check left < right
      return leftVal < rightVal || leftVal == rightVal ? null : { invalidMemory: true };
    };
  }

  nodeSelectionChange(dep) {
    if (this.podForm.controls['nodeNames']?.value?.length === 0) {
      return false; // enable all options if no option selectes
    }
    if (this.podForm.controls['nodeNames']?.value?.includes('any')) {
      return dep !== 'any'; // disable all options except 'any' if 'any' is selected
    } else if (!this.podForm.controls['nodeNames']?.value?.includes('any')) {
      return dep === 'any'; // disable 'any' if other than 'any' selected
    }
    return false;
  }
}
